import LocalPlayerPortrait, {LocalPortraits} from './LocalPlayerPortrait';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlayerProfileWidget } from "./LineUp";
import { HockeyPlayerProfile } from "../sports/HockeyLineups";
import { HQ } from "../sportConfig";
import "./TeamSquad.css";
import TeamStaff from "./TeamStaff";
import { useSearchParams } from "react-router-dom";

const positionGroup = (position) => {
  const value = String(position || "").toLocaleLowerCase("sv-SE");
  if (/målvakt|goalkeeper|^gk?$/.test(value)) return "Målvakter";
  if (/back|defen|^(?:ld|rd|d)$/.test(value)) return "Backar";
  if (/mittfält|midfield/.test(value)) return "Mittfältare";
  if (/anfall|forward|center|^(?:ce|c|f|lw|rw)$/.test(value)) return HQ.sport === "football" ? "Anfallare" : "Forwards";
  return "Övriga spelare";
};

export default function TeamSquad({ simulation, teamId, season }) {
  const [params] = useSearchParams();
  const requestedPlayer = params.get('player');
  const openedPlayer = useRef(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const trigger = useRef(null);
  const closeProfile = useCallback(() => { setSelectedPlayer(null); requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true })); }, []);
  const [open, setOpen] = useState(Boolean(requestedPlayer));
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!open || !simulation) return;
    const controller = new AbortController();
    setError(false);
    setData(null);
    const query = new URLSearchParams({ sport: HQ.sport, simulation, team_id: teamId });
    fetch(`/sportsim/api/team-squad?${query}`, { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error(); return response.json(); })
      .then(value => { if (!controller.signal.aborted) setData(value); })
      .catch(failure => { if (failure.name !== "AbortError" && !controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [open, simulation, teamId, attempt]);
  const groups = ["Målvakter", "Backar", "Mittfältare", HQ.sport === "football" ? "Anfallare" : "Forwards", "Övriga spelare"]
    .map(label => ({ label, players: (data?.players || []).filter(player => positionGroup(player.position) === label)
      .sort((a, b) => (Number(a.number) || 999) - (Number(b.number) || 999) || a.name.localeCompare(b.name, "sv")) }))
    .filter(group => group.players.length);
  const selectPlayer = useCallback((player, event) => {
    trigger.current = event?.currentTarget || document.querySelector('.team-squad summary');
    const query = new URLSearchParams({ sport: HQ.sport, simulation, team_id: teamId });
    const profileUrl = `/sportsim/api/team-squad/player/${encodeURIComponent(player.id)}?${query}`;
    setSelectedPlayer({ ...player, team_name: data.team, team: data.team, simulation,
      group: positionGroup(player.position), number: player.number || "–",
      ...(HQ.sport === "hockey" ? { squad_profile_url: profileUrl } : HQ.sport === "football" ? { profile_url: profileUrl } : {}) });
  }, [data, simulation, teamId]);
  useEffect(() => {
    if (!requestedPlayer || !data || openedPlayer.current === requestedPlayer) return;
    const player = data.players?.find(person => String(person.id) === requestedPlayer);
    if (player) { openedPlayer.current = requestedPlayer; selectPlayer(player); }
    // Only resolve the exact requested identity from the verified squad response.
  }, [requestedPlayer, data, selectPlayer]);
  const localPlayers = (data?.players || []).map(player => ({
    ...player,
    squad_profile_url: `/sportsim/api/team-squad/player/${encodeURIComponent(player.id)}?${new URLSearchParams({sport: HQ.sport, simulation, team_id: teamId})}`,
  }));
  return <LocalPortraits players={localPlayers} context={{simulation, team: data?.team || ""}}><details className="team-squad team-page__panel" open={open} onToggle={event => setOpen(event.currentTarget.open)}>
    <summary><span>Truppen{season && <small>{season}</small>}</span><span className="team-squad__count">{data?.status === "available" ? `${data.players.length} spelare` : ""}</span></summary>
    <div className="team-squad__content">
      {requestedPlayer && data?.status === 'available' && !data.players.some(person => String(person.id) === requestedPlayer) && <p role="status">Spelaren finns inte längre i den här truppen.</p>}
      {!simulation || data?.status === "unavailable" ? <p>Truppen är inte tillgänglig för den här säsongen.</p>
        : error ? <div role="alert"><p>Truppen kunde inte hämtas just nu.</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Försök igen</button></div>
          : !data ? <p role="status">Hämtar truppen…</p>
            : data.status === "empty" ? <p>Inga spelare är publicerade för laget ännu.</p>
              : groups.map(group => <section key={group.label} aria-label={group.label}>
                <h3>{group.label}</h3><ul>{group.players.map(player => <li key={player.id}><button className="team-squad__player" type="button" onClick={event => selectPlayer(player, event)} aria-label={`Visa profil för ${player.name}`}>
                  <LocalPlayerPortrait player={player}/>
                  <span className="team-squad__number">{player.number || "–"}</span>
                  <span className="team-squad__name">{player.name}</span>
                  {player.birth_year && <span className="team-squad__year" aria-label={`Född ${player.birth_year}`}>{player.birth_year}</span>}
                </button></li>)}</ul>
              </section>)}
      {data && ["available", "empty"].includes(data.status) && <TeamStaff staff={data.staff} status={data.staff_status} />}
      {data?.stale && <p className="team-squad__notice">Senast hämtade trupp visas. Källan kan inte uppdateras just nu.</p>}
      {data?.source_url && <a className="team-squad__source" href={data.source_url} target="_blank" rel="noreferrer">Se hos {HQ.sport === "floorball" ? "Svensk Innebandy" : HQ.sport === "hockey" ? "Swehockey" : "Allsvenskan"} <span aria-hidden="true">↗</span></a>}
    </div>
  </details>
    {selectedPlayer && (HQ.sport === "hockey"
      ? <HockeyPlayerProfile key={selectedPlayer.id} player={selectedPlayer} onClose={closeProfile} />
      : <PlayerProfileWidget key={selectedPlayer.id} player={selectedPlayer} onClose={closeProfile}
          onPositionChange={(position, original) => setSelectedPlayer(current => ({ ...current, position: position || original, ibis_position: original }))} />)}
  </LocalPortraits>;
}
